import { useCallback, useEffect, useRef, useState } from 'react';
import { FaceText, GameFrame, shuffle, useGame, usePaused, useRoundStyle } from '../sdk/react';

const FEEDBACK_HOLD_MS = 700;

type Item = { say: string; answer: string };

type Round = {
  roundId: string | null;
  say: string;
  answer: string;
  promptType: 'AUDIO' | 'TEXT';
  tiles: string[];
  errorless: boolean;
  probe: boolean;
  repeatSeconds: number | null;
};

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve',
  'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];

function gridClass(n: number): string {
  if (n <= 4) return 'cols-2';
  if (n <= 9) return 'cols-3';
  return 'cols-4';
}

function inAnswerForm(word: string, answer: string): string {
  const n = NUMBER_WORDS.indexOf(word.toLowerCase());
  if (n >= 0 && /^\d+$/.test(answer.trim())) return String(n);
  return word.toLowerCase() === answer.trim().toLowerCase() ? answer.trim() : word;
}

function namedOptions(say: string, answer: string): string[] {
  const m = /([\p{L}\p{N}'-]+)\s+or\s+([\p{L}\p{N}'-]+)\s*\?\s*$/iu.exec(say.trim());
  if (!m) return [];
  const first = inAnswerForm(m[1], answer);
  const second = inAnswerForm(m[2], answer);
  const a = answer.trim().toLowerCase();
  if (first.toLowerCase() === second.toLowerCase()) return [];
  if (first.toLowerCase() !== a && second.toLowerCase() !== a) return [];
  return [first, second];
}

function localRound(items: Item[], tileCount: number, promptType: 'AUDIO' | 'TEXT', lastAnswer?: string): Round {
  let pool = items;
  if (lastAnswer && items.length > 1) {
    pool = items.filter((it) => it.answer !== lastAnswer);
    if (pool.length === 0) pool = items;
  }
  const target = pool[Math.floor(Math.random() * pool.length)];
  const base = { roundId: null, say: target.say, promptType, errorless: false, probe: false, repeatSeconds: null };
  const named = namedOptions(target.say, target.answer);
  if (named.length > 0) return { ...base, answer: target.answer.trim(), tiles: shuffle(named) };
  const others = [...new Set(items.filter((it) => it.answer !== target.answer).map((it) => it.answer))];
  const want = Math.max(0, Math.min(tileCount - 1, others.length));
  return { ...base, answer: target.answer, tiles: shuffle([target.answer, ...shuffle(others).slice(0, want)]) };
}

function serverRound(r: Record<string, unknown>): Round {
  const say = typeof r.promptText === 'string' ? r.promptText : '';
  return {
    roundId: typeof r.roundId === 'string' ? r.roundId : null,
    say,
    answer: typeof r.expected === 'string' ? r.expected : say,
    promptType: r.promptType === 'TEXT' ? 'TEXT' : 'AUDIO',
    tiles: Array.isArray(r.tiles) ? r.tiles.filter((t): t is string => typeof t === 'string') : [],
    errorless: r.errorless === true,
    probe: r.probe === true,
    repeatSeconds: typeof r.repeatSeconds === 'number' ? r.repeatSeconds : null,
  };
}

export default function TileMatch() {
  const game = useGame();
  const { kit } = game;
  const live = kit.rounds.available;
  const items = (Array.isArray(game.config.items) ? game.config.items : [])
    .filter((it): it is Item => !!it && typeof (it as Item).say === 'string' && typeof (it as Item).answer === 'string');
  const empty = !live && items.length === 0;
  const [round, setRound] = useState<Round | null>(null);
  const [correctTile, setCorrectTile] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const shownAt = useRef(0);
  const prompts = useRef(0);
  const lastAnswer = useRef<string | undefined>(undefined);
  const paused = usePaused();
  const { style, typeClass } = useRoundStyle();

  const start = useCallback(async () => {
    setCorrectTile(null);
    setCompleted(false);
    kit.speech.stop();
    if (live) {
      const r = await kit.rounds.start();
      if (!r) return;
      kit.session.offerBreak(r.breakOffered === true);
      setRound(serverRound(r));
      return;
    }
    if (items.length === 0) return;
    const next = localRound(items, game.num('tileCount', 4), game.textPrompt ? 'TEXT' : 'AUDIO', lastAnswer.current);
    lastAnswer.current = next.answer;
    setRound(next);
  }, [kit, live, game.config]);

  useEffect(() => { void start(); }, [start]);

  const playPrompt = useCallback(() => {
    if (!round) return;
    prompts.current += 1;
    if (round.promptType === 'AUDIO') kit.speech.say(round.say);
  }, [kit, round]);
  const playRef = useRef(playPrompt);
  playRef.current = playPrompt;

  useEffect(() => {
    if (!round) return;
    shownAt.current = kit.clock.now();
    prompts.current = 0;
    const t = window.setTimeout(() => playRef.current(), 350);
    return () => window.clearTimeout(t);
  }, [round]);

  const wasPaused = useRef(paused);
  useEffect(() => {
    if (wasPaused.current && !paused && round && !completed) playRef.current();
    wasPaused.current = paused;
  }, [paused]);

  useEffect(() => {
    if (!round || completed || paused) return;
    const seconds = Math.max(kit.prefs.promptRepeatMinSeconds, round.repeatSeconds ?? kit.prefs.promptRepeatSeconds);
    const id = window.setInterval(() => playRef.current(), seconds * 1000);
    return () => window.clearInterval(id);
  }, [round, completed, paused]);

  const [fading, setFading] = useState(false);
  useEffect(() => {
    if (!completed) return;
    const fade = window.setTimeout(() => setFading(true), FEEDBACK_HOLD_MS);
    const advance = window.setTimeout(() => { void start().finally(() => setFading(false)); }, FEEDBACK_HOLD_MS + game.transitionMs);
    return () => { window.clearTimeout(fade); window.clearTimeout(advance); };
  }, [completed]);

  async function pick(tile: string) {
    if (!round || completed) return;
    const answer = round.answer.trim().toLowerCase();
    if (round.errorless && tile.trim().toLowerCase() !== answer) return;
    if (round.roundId && live) {
      const result = await kit.rounds.attempt(round.roundId, {
        tile,
        respondedMs: Math.round(kit.clock.elapsedSince(shownAt.current)),
        promptsPlayed: prompts.current,
      });
      if (!result) return;
      if (result.correct === true) {
        setCorrectTile(tile);
        setCompleted(true);
      } else playPrompt();
      return;
    }
    if (tile === round.answer) {
      setCorrectTile(tile);
      setCompleted(true);
      kit.sound.correct();
    } else playPrompt();
  }

  return (
    <GameFrame game={game} empty={empty} emptyText="Edit it to add words to match." onResume={() => { void start(); }} breakTrial={round?.roundId ?? null}>
      {round && (
        <>
          {round.probe && (
            <div className="banner" role="status">
              First go today, so no help is given and nothing is earned. It shows what they can do on their own.
            </div>
          )}
          {round.promptType === 'TEXT' && <div className="concept-prompt-text" aria-live="polite">{round.say}</div>}
          <div
            className={`tile-grid ${gridClass(round.tiles.length)} ${typeClass}${fading ? ' fading-out' : ''}`}
            style={style}
            role="group"
            aria-label="Word tiles"
            aria-live="polite"
          >
            {round.tiles.map((tile, i) => (
              <button
                key={`${tile}-${i}`}
                className={`tile${completed && correctTile === tile ? ' correct' : ''}`}
                onClick={() => void pick(tile)}
                disabled={completed}
                aria-label={`Tile: ${tile}`}
              >
                <FaceText text={tile} />
              </button>
            ))}
          </div>
        </>
      )}
    </GameFrame>
  );
}
